import { scrollToAnchor, throttle } from '@cian/newbuilding-utils';
import { useUpdateEffect } from '@cian/react-utils';
import { Tabs } from '@cian/ui-kit/prev';

import { clsx } from 'clsx';
import { type ReactElement, forwardRef, useCallback, useEffect, useState } from 'react';

import { type INavLinks, type TSectionAnchor } from 'shared/settings/routes';
import { setHash } from 'shared/utils/setHash';

import { Layout } from '../Layout';

import { getNavLinksFromSections } from './internal/utils/getNavLinksFromSections';
import { updateActiveSection } from './internal/utils/updateActiveSection';

import styles from './Header.css';

export interface IHeaderProps {
  isFixed: boolean;
  number?: ReactElement;
  callback?: ReactElement;
  logo?: ReactElement;
  onNavSectionClick?: (section: TSectionAnchor) => void;
}

export const Header = forwardRef<HTMLElement, IHeaderProps>(function Header(props, ref) {
  const { isFixed, callback, logo, number, onNavSectionClick } = props;
  const [navLinks, setNavLinks] = useState<INavLinks[]>([]);
  const [activeNavLink, setActiveNavLink] = useState<TSectionAnchor>('#');

  const handleActiveNavLinkChange = useCallback(
    (value: TSectionAnchor) => {
      onNavSectionClick?.(value);

      const anchor = `#${value}`;
      const event = new Event('click');

      scrollToAnchor({ anchor, hasRelocation: false, offset: 50 })(event);

      setTimeout(() => {
        setActiveNavLink(value);
        setHash(value);
      }, 600);
    },
    [onNavSectionClick],
  );

  useEffect(() => {
    setNavLinks(getNavLinksFromSections());
  }, []);

  useUpdateEffect(() => {
    if (navLinks.length) {
      const sectionsChangeListener = throttle(600, () => updateActiveSection(navLinks, setActiveNavLink));

      window.addEventListener('scroll', sectionsChangeListener);

      return (): void => {
        window.removeEventListener('scroll', sectionsChangeListener);
      };
    }

    return undefined;
  }, [navLinks]);

  return (
    <header className={clsx(styles['header'], { [styles['header_fixed']]: isFixed })} ref={ref}>
      <Layout>
        <div className={styles['header__wrapper']}>
          <nav className={styles['navigation']}>
            {logo && <div className={styles['navigation__logo']}>{logo}</div>}

            <Tabs activeTab={activeNavLink} tabs={navLinks} onChange={handleActiveNavLinkChange} />
          </nav>

          <div className={styles['header__contacts']}>
            {number}
            {callback}
          </div>
        </div>
      </Layout>
    </header>
  );
});
